import React, { useEffect, useState, useCallback } from "react";
import "./styles.scss";
import axios from "axios";
import { toast } from "react-toastify";
import ReactModal from "react-modal";
import MobileVerifyOtp from "./MobileVerifyOtp";
import { useConsentMasterQuery } from "../../Services/vehicle/Vehicle";
import { CUST_EMAIl, CUST_NAME, CUST_PHONE } from "../../constants";
import LoadingSpinner from "../../components/LoadingSpinner";

const CustomerProfile = () => {
  const [loading, setLoading] = useState(true);
  const [checkValue, setCheckValue] = useState<any>({});
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [address, setAddress] = useState("");
  const [otpPageOpen, setOtpPageOpen] = useState(false);
  const [consents, setConsents] = useState([]);

  const [consent, setConsent] = useState({
    termsCondition: false,
    contacts_agreement: false,
    notify_number: false,
    notify_email: false,
    notify_phone: false,
    consent_ids: [],
  });

  const { data: consentMasterData } = useConsentMasterQuery({});

  const fetchingTenant = window.location.hostname.split(".")[0];
  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const token = localStorage.getItem("customer-token");

  // Centralized fetchProfile function
  const fetchProfile = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc/profile/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCheckValue(response?.data);
      console.log("Profile loaded:", response?.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [token, fetchingTenant, apiPort]);

  // Initial profile fetch on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (consentMasterData) {
      setConsents(consentMasterData?.data);
    }
  }, [consentMasterData]);

  // Load consent data from profile
  useEffect(() => {
    if (checkValue && Object.keys(checkValue).length > 0) {
      setConsent((prev) => ({
        termsCondition: checkValue.termsCondition === 1,
        contacts_agreement: checkValue.contacts_agreement === 1,
        notify_email: checkValue.notify_email === 1,
        notify_phone: checkValue.notify_phone === 1,
        notify_number: checkValue.notify_number === 1,
        consent_ids: Array.isArray(checkValue.consent_ids)
          ? checkValue.consent_ids.map((id: any) => String(id))
          : checkValue.consent_ids
          ? [String(checkValue.consent_ids)]
          : [],
      }));
    }
  }, [checkValue]);

  // Load profile data into form fields
  useEffect(() => {
    if (checkValue && Object.keys(checkValue).length > 0) {
      setEmail(checkValue?.email || "");
      setMobileNumber(checkValue?.phone || "");
      setState(checkValue?.state || "");
      setCity(checkValue?.city || "");
      setPincode(checkValue?.pincode || "");
      setName(checkValue?.first_name || "");
      setLastName(checkValue?.last_name || "");
      setAddress(checkValue?.address || "");
    }
  }, [checkValue]);

  const updateHandler = async () => {
    const hasPhoneChanged = mobileNumber !== checkValue?.phone;

    if (hasPhoneChanged) {
      updatePhoneNumber();
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc/profile/`,
        {
          first_name: name,
          last_name: lastName,
          email: email,
          phone: mobileNumber,
          address: address,
          city: city,
          state: state,
          country: "USA",
          pincode: pincode,
          lat: "40.7128",
          lang: "-74.0060",
          notify_email: consent.notify_email ? 1 : 0,
          notify_phone: consent.notify_phone ? 1 : 0,
          notify_number: consent.notify_number ? 1 : 0,
          contacts_agreement: consent.contacts_agreement ? 1 : 0,
          termsCondition: consent.termsCondition ? 1 : 0,
          consent_ids: consent.consent_ids,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Profile and consents saved successfully!");
      console.log("Profile updated:", response.data);
      await fetchProfile();
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
      if (error.response?.data) {
        const errorMessages = Object.values(error.response.data).flat();
        errorMessages.forEach((message: any) => {
          toast.error(Array.isArray(message) ? message[0] : message);
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const updatePhoneNumber = async () => {
    setLoading(true);

    const updatedPhone = {
      old_phone: checkValue?.phone,
      new_phone: mobileNumber,
    };

    try {
      const response = await axios.post(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc/change-number/request-otp/`,
        updatedPhone,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data) {
        toast.success("OTP sent successfully");
        setOtpPageOpen(true);
      }
    } catch (error: any) {
      console.error("Error requesting OTP:", error);

      // 🔥 Handle backend error message
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to send OTP");
      }
    } finally {
      setLoading(false);
    }
  };

  // Callback for OTP verification success - REFETCH PROFILE
  const handleOtpVerified = useCallback(async () => {
    toast.success("Phone number updated successfully!");
    setOtpPageOpen(false);
    await fetchProfile();
  }, [fetchProfile]);

  const customModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "450px",
    },
  };

  // Helper function to check if a consent is checked
  const isConsentChecked = (el: any) => {
    const booleanConsentFields = [
      "termsCondition",
      "contacts_agreement",
      "notify_number",
      "notify_email",
      "notify_phone",
    ];

    if (el.input_name && booleanConsentFields.includes(el.input_name)) {
      return !!consent[el.input_name as keyof typeof consent];
    } else {
      return (consent.consent_ids || []).includes(String(el.id));
    }
  };

  const handleConsentChange = (el: any) => {
    const booleanConsentFields = [
      "termsCondition",
      "contacts_agreement",
      "notify_number",
      "notify_email",
      "notify_phone",
    ];

    if (el.input_name && booleanConsentFields.includes(el.input_name)) {
      // Toggle boolean consent
      setConsent((prev) => ({
        ...prev,
        [el.input_name]: !prev[el.input_name as keyof typeof consent],
      }));
    } else {
      // Toggle consent_ids
      if (el.id) {
        setConsent((prev) => {
          const consentIds = new Set(prev.consent_ids || []);
          const consentId = String(el.id);

          if (consentIds.has(consentId)) {
            consentIds.delete(consentId);
          } else {
            consentIds.add(consentId);
          }

          const newConsentIds = Array.from(consentIds).filter(
            Boolean
          ) as string[];

          console.log(`Toggling consent "${el.name}" (ID: ${consentId})`);
          console.log("Previous consent_ids:", prev.consent_ids);
          console.log("New consent_ids:", newConsentIds);

          return {
            ...prev,
            consent_ids: newConsentIds,
          };
        });
      }
    }
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      <div className="personal-info-card">
        <h3 className="form-title">Personal Information</h3>
        <form
          className="personal-info-form"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="form-group">
            <label>
              First Name <span>*</span>
            </label>
            <input
              value={name}
              type="text"
              placeholder="Enter First Name"
              onChange={(e) => {
                const filteredValue = e.target.value.replace(
                  /[^a-zA-Z\s]/g,
                  ""
                );
                setName(filteredValue);
              }}
            />
          </div>
          <div className="form-group">
            <label className="required">Last Name</label>
            <input
              value={lastName}
              type="text"
              placeholder="Enter Last Name"
              onChange={(e) => {
                const filteredValue = e.target.value.replace(
                  /[^a-zA-Z\s]/g,
                  ""
                );
                setLastName(filteredValue);
              }}
            />
          </div>
          <div className="form-group">
            <label>
              Email <span>*</span>
            </label>
            <input
              disabled={!!checkValue?.email}
              value={email}
              type="email"
              placeholder="Enter Email"
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </div>
          <div className="form-group" style={{ position: "relative" }}>
            <span style={{ display: "flex", flexDirection: "column" }}>
              <label className="required">Mobile Number</label>
              <input
                value={mobileNumber}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                placeholder="Enter Mobile Number"
                onChange={(e) => {
                  const filteredValue = e.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 10);
                  setMobileNumber(filteredValue);
                }}
              />
            </span>
          </div>
          <div className="form-group">
            <label className="">State</label>
            <input
              value={state}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(
                  /[^a-zA-Z\s]/g,
                  ""
                );
                setState(filteredValue);
              }}
              type="text"
              placeholder="Enter State"
            />
          </div>
          <div className="form-group">
            <label>City</label>
            <input
              value={city}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(
                  /[^a-zA-Z\s]/g,
                  ""
                );
                setCity(filteredValue);
              }}
              type="text"
              placeholder="Enter City"
            />
          </div>
          <div className="form-group">
            <label>PIN Code</label>
            <input
              value={pincode}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                setPincode(onlyNums.slice(0, 6));
              }}
              type="text"
              inputMode="numeric"
              placeholder="Enter PIN Code"
            />
          </div>
          <div className="form-group address">
            <label>Address</label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              type="text"
              placeholder="Enter Address"
            />
          </div>
        </form>

        <div
          className="consent-section"
          style={{ borderTop: "1px solid #D7D7D7", padding: "10px 0" }}
        >
          <h3 className="consent-section-title">Communication & Consent :</h3>
          <div className="consent-list-cont">
            {consents?.map((el: any) => (
              <div className="consent-list" key={el.id}>
                <label className="consent-item">
                  <input
                    type="checkbox"
                    checked={isConsentChecked(el)}
                    onChange={() => handleConsentChange(el)}
                  />
                  <span className="checkmark"></span>
                  {el?.name}
                  {el?.is_mandatory && (
                    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                  )}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div
          className="profile-save-button"
          style={{ display: "flex", justifyContent: "end" }}
        >
          <button
            onClick={updateHandler}
            disabled={loading}
            style={{
              color: "#FFFFFF",
              background: loading ? "#ccc" : "#F25D21",
              border: "none",
              padding: "10px 15px",
              borderRadius: "20px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>

        <ReactModal
          isOpen={otpPageOpen}
          onRequestClose={() => setOtpPageOpen(false)}
          style={customModalStyles}
          contentLabel="Verify OTP"
        >
          <MobileVerifyOtp
            resendOtp={updatePhoneNumber}
            mobileNumber={mobileNumber}
            setOpenOtpModal={setOtpPageOpen}
            onOtpVerified={handleOtpVerified}
          />
        </ReactModal>
      </div>
    </>
  );
};

export default CustomerProfile;
